class SearchComponent extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearch = this.querySelector('.predictive-search');
    this.searchToggle = document.querySelector('#search-toggle');
    this.closeButton = this.querySelector('.search-input__container-icon-close');
    this.suggestionsContainer = this.querySelector('.suggestions_search_container');

    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    this.scrollHandler = this.handleScroll.bind(this);

    this.input.addEventListener(
      'input',
      this.debounce(() => {
        this.onChange();
      }, 300).bind(this),
    );

    this.input.addEventListener('focus', this.addGlobalEventListeners.bind(this));

    // Close search when close button is clicked
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.closeSearch();
      });
    }

    // Listen for search toggle changes
    if (this.searchToggle) {
      this.searchToggle.addEventListener('change', this.handleToggleChange.bind(this));
    }

    this.initEventListeners();
  }

  initEventListeners() {
    this.predictiveSearch.addEventListener('click', event => {
      const closeButton = event.target.closest('.predictive-search__title svg');
      if (closeButton) {
        this.closePredictiveSearch();
        event.stopPropagation();
      }
    });
  }

  addGlobalEventListeners() {
    if (this.predictiveSearch.hasAttribute('open')) {
      document.addEventListener('click', this.clickOutsideHandler);
      window.addEventListener('scroll', this.scrollHandler);
    }
    
    // Add listeners when search container is open
    if (this.searchToggle && this.searchToggle.checked) {
      document.addEventListener('click', this.clickOutsideHandler);
      window.addEventListener('scroll', this.scrollHandler);
    }
  }

  removeGlobalEventListeners() {
    document.removeEventListener('click', this.clickOutsideHandler);
    window.removeEventListener('scroll', this.scrollHandler);
  }

  handleClickOutside(event) {
    const target = event.target;

    if (!this.contains(target) && this.predictiveSearch.hasAttribute('open')) {
      this.closePredictiveSearch();
    }

    // Close search container if clicking outside
    if (!this.contains(target) && !target.closest('.header__icon--search') && this.searchToggle && this.searchToggle.checked) {
      this.closeSearch();
    }
  }

  handleScroll() {
    if (this.predictiveSearch.hasAttribute('open')) {
      this.closePredictiveSearch();
    }
    
    // Close search container on scroll
    if (this.searchToggle && this.searchToggle.checked) {
      this.closeSearch();
    }
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (searchTerm.length > 0) {
      this.getSearchResults(searchTerm);
      document.addEventListener('click', this.clickOutsideHandler);
      window.addEventListener('scroll', this.scrollHandler);
    } else {
      this.closePredictiveSearch();
      this.showSuggestions();
    }
  }

  getSearchResults(searchTerm) {
    fetch(`/search/suggest?q=${searchTerm}&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          console.error('Network response was not ok', response.statusText);
        }
        return response.text();
      })
      .then(text => {
        const htmlParser = new DOMParser();
        const parsedDocument = htmlParser.parseFromString(text, 'text/html');

        const resultsMarkup = parsedDocument.querySelector('.predictive-search')?.innerHTML;
        const resultsCount = parsedDocument.querySelector('ul')?.innerHTML;

        if (resultsMarkup) {
          this.predictiveSearch.innerHTML = resultsMarkup;
        }

        if (resultsCount) {
          this.openPredictiveSearch();
          this.hideSuggestions();
        } else {
          this.closePredictiveSearch();
          this.showSuggestions();
        }

        const predictiveSearchClose = this.predictiveSearch.querySelector('.predictive-search__title svg');
        if (predictiveSearchClose) {
          predictiveSearchClose.addEventListener('click', () => {
            this.closePredictiveSearch();
          });
        }
      })
      .catch(error => {
        console.error('Error fetching search results:', error);
      });
  }

  openPredictiveSearch() {
    this.predictiveSearch.setAttribute('open', '');
    document.addEventListener('click', this.clickOutsideHandler);
    window.addEventListener('scroll', this.scrollHandler);
  }

  closePredictiveSearch() {
    this.predictiveSearch.removeAttribute('open');
    this.removeGlobalEventListeners();
    this.showSuggestions();
  }

  closeSearch() {
    if (this.searchToggle) {
      this.searchToggle.checked = false;
    }
    this.closePredictiveSearch();
    this.showSuggestions();
  }

  hideSuggestions() {
    if (this.suggestionsContainer) {
      this.suggestionsContainer.classList.add('hidden');
    }
  }

  showSuggestions() {
    if (this.suggestionsContainer) {
      this.suggestionsContainer.classList.remove('hidden');
    }
  }

  handleToggleChange() {
    if (this.searchToggle && this.searchToggle.checked) {
      // Focus input when search opens
      setTimeout(() => {
        if (this.input) {
          this.input.focus();
        }
      }, 100);
      this.addGlobalEventListeners();
      this.showSuggestions();
    } else {
      this.closePredictiveSearch();
      this.removeGlobalEventListeners();
    }
  }

  debounce(fn, wait) {
    let timeoutId = null;

    return (...args) => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        fn.apply(this, args);
        timeoutId = null;
      }, wait);
    };
  }

  disconnectedCallback() {
    this.removeGlobalEventListeners();
  }
}

customElements.define('search-component', SearchComponent);